/*****************************************************
Naam: Login.jsx
Datum: 2025-12-29
Versie: 3.1.0
Auteur: Ragnar Standhardt
******************************************************
Beschrijving:
Login scherm voor toegang tot het dashboard.

CHANGELOG v3.1.0: 🎨 SHARP CORPORATE DESIGN
- UI: Scherpe hoeken overal (geen border-radius)
- UI: Navy gradient achtergrond
- UI: Cyan top-bar accent op login card
- UI: Scherpe input fields en button
- BEHOUDEN: Alle functionaliteit

Input: Geen
Output: Redirect naar Dashboard bij succesvolle login
******************************************************/

import React, { useState, useEffect } from 'react';

const Login = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('Dashboard');

  useEffect(() => {
    const logo = localStorage.getItem('companyLogo');
    if (logo) setCompanyLogo(logo);
    const loadCompanyName = async () => {
      try {
        const response = await fetch('/api/company-info');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.bedrijfsnaam) setCompanyName(data.data.bedrijfsnaam);
        }
      } catch (error) {
      }
    };
    loadCompanyName();
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch('/api/check-session');
        const result = await response.json();
        if (result.authenticated) onLoginSuccess();
      } catch (error) {
      }
    };
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const result = await response.json();
      if (result.success) {
        onLoginSuccess();
      } else {
        setError(result.message || 'Ongeldige gebruikersnaam of wachtwoord');
      }
    } catch (error) {
      setError('Fout bij inloggen. Probeer het opnieuw.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #0f2233 0%, #1a364c 50%, #2a4a64 100%)' }}
    >
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 75% 25%, rgba(194,230,239,0.06) 0%, transparent 50%)' }}
      />
      <div className="w-full max-w-md relative"
        style={{ boxShadow: '0 12px 48px rgba(0,0,0,0.35)' }}
      >
        {/* Top cyan accent */}
        <div style={{ height: '4px', background: '#c2e6ef' }} />

        <div style={{ backgroundColor: '#ffffff', padding: '32px' }}>
          {/* Logo / Branding */}
          <div className="text-center mb-8">
            {companyLogo ? (
              <div className="flex justify-center mb-4">
                <img src={companyLogo} alt="Company Logo" className="h-20 w-auto object-contain" />
              </div>
            ) : (
              <div className="flex justify-center mb-4">
                <div style={{ background: '#1a364c', padding: '10px 24px' }}>
                  <span style={{ fontSize: '28px', fontWeight: 800, color: '#c2e6ef', letterSpacing: '-0.5px' }}>
                    {companyName === 'Dashboard' ? 'sendwell.' : companyName}
                  </span>
                </div>
              </div>
            )}
            <div style={{ fontSize: '13px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Dashboard Login</div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#1a364c', marginBottom: '6px' }}>Gebruikersnaam</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                className="w-full outline-none transition-all"
                style={{ padding: '10px 14px', border: '1.5px solid #e2e8f0', fontSize: '14px' }}
                onFocus={(e) => { e.target.style.borderColor = '#c2e6ef'; e.target.style.boxShadow = '0 0 0 3px rgba(194,230,239,0.3)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                placeholder="Voer gebruikersnaam in" required autoFocus
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#1a364c', marginBottom: '6px' }}>Wachtwoord</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full outline-none transition-all"
                style={{ padding: '10px 14px', border: '1.5px solid #e2e8f0', fontSize: '14px' }}
                onFocus={(e) => { e.target.style.borderColor = '#c2e6ef'; e.target.style.boxShadow = '0 0 0 3px rgba(194,230,239,0.3)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                placeholder="Voer wachtwoord in" required
              />
            </div>

            {error && (
              <div style={{ backgroundColor: '#fee2e2', border: '1px solid #fecaca', color: '#dc2626', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' }}>
                ⚠️ {error}
              </div>
            )}

            <button type="submit" disabled={isLoading} className="w-full transition-all"
              style={{
                background: isLoading ? '#94a3b8' : '#1a364c',
                color: '#ffffff', padding: '12px 0', fontWeight: 700, fontSize: '14px',
                border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer',
                borderBottom: isLoading ? 'none' : '3px solid #c2e6ef',
              }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Inloggen...
                </span>
              ) : 'Inloggen'}
            </button>
          </form>

          <div className="mt-6 text-center" style={{ fontSize: '11px', color: '#94a3b8' }}>
            Geen toegang? Neem contact op met de beheerder.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
